// PeekPanelTabs.jsx


import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodePeek } from 'context/NodePeekContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Hooks
import useNodeActivityCount from 'node/hooks/useNodeActivityCount';
import useNodeData from 'node/hooks/useNodeData';
import useNodeDescendants from 'node/hooks/useNodeDescendants';
import useNodeForms from 'node/hooks/useNodeForms';
import useNodeRelatedGroups from 'node/hooks/useNodeRelatedGroups';
import useNodeViewToolbarState from 'node/hooks/useNodeViewToolbarState';
import useRelatedGrandchildNodes from 'node/hooks/useRelatedGrandchildNodes';

// Services
import { DEFAULT_FORM_KEY } from 'services/forms/FormModel';
import { createView } from 'services/views/ViewWriteService';

// Utilities
import {
  buildFiltersFromLensSelections,
  suggestOrganicViewTitle,
} from 'views/utils/organicViews';

// Components
import { toast } from 'react-toastify';
import SaveOrganicViewModal from 'components/viewtoolbar/SaveOrganicViewModal';
import ViewToolbar from 'components/viewtoolbar/ViewToolbar';
import NodeAboutForms from 'node/forms/NodeAboutForms';
import NodeDetails from 'node/details/NodeDetails';
import NodeForms from 'node/forms/NodeForms';
import NodeTypeViewer from 'node/highlight/NodeTypeViewer';
import { shouldShowTypeViewerTab } from 'node/highlight/nodeTypeViewerRegistry';
import NodeMissions from 'node/missions/NodeMissions';
import ActivityRailResizeHandle from 'node/modal/ActivityRailResizeHandle';
import PeekActivitySurface from 'node/peek/activity/PeekActivitySurface';
import NodeTabsBar from 'node/tabs/NodeTabsBar';
import {
  UNIFIED_TAB_KEYS,
  isRelatedTypeTabKey,
} from 'node/tabs/unifiedNodeTabKeys';
import NodeViewContent from 'node/views/NodeViewContent';
import ProjectFloorPlan from 'nodetypes/project/floorplan/ProjectFloorPlan';
import ViewModal from 'views/modal/ViewModal';

const normalizePeekTypeKey = (value) => String(value || '').trim().toUpperCase();

const NOOP_SET_PENDING_AUTO_TAB = () => {};

// Cover lives above the title; comments/activity are Peek chrome (tab/rail).
const PEEK_ABOUT_EXCLUDED_FIELD_TYPES = ['cover', 'comments', 'activity'];

const isDefaultFormTemplate = (form) => (
  Boolean(form?.isDefault) || String(form?.key || '').trim() === DEFAULT_FORM_KEY
);

/**
 * PeekPanelTabs
 *
 * Tab shell for Peek / Open modal (About, Activity, Forms, related types, Highlight).
 * @param {'tab'|'rail'|'none'} [activityPlacement='tab'] - Activity as a tab, right rail, or hosted externally.
 * @param {string} [activeTabKey] - Optional controlled tab key (defaults to Peek context).
 * @param {function} [setActiveTabKey] - Optional controlled tab setter (defaults to Peek context).
 * @param {number} [activityRailWidthPx] - Rail width when placement is rail.
 * @param {function} [onActivityRailWidthChange]
 * @param {function} [onActivityRailWidthCommit]
 * @param {boolean|null} [includeFormsTab] - When false, hide Forms (Workspace read mode).
 * @param {boolean} [includeFloorPlanTab=false] - Floor Plan is NodeWorkspace-only; Peek keeps this false.
 * @param {boolean} [aboutHostsForms=false] - When true (Workspace), About hosts FormRenderer + chips; Forms tab stays hidden.
 * @param {'peek'|'workspace'} [typeViewerSurface='peek'] - Which type viewers may force the Highlight-slot tab.
 * @param {boolean} [showViewToolbar=true] - When false, hide related-tab ViewToolbar (Workspace rail).
 * @param {boolean} [organicViewsEnabled=false] - Enable organic Board / Lens / Save on this surface.
 * @param {function|null} [onRelatedNodeOpen] - Optional open handler for related items (defaults to Peek).
 * @param {'row'|'wrap'} [tabsLayout='row'] - Tab layout; wrap avoids SelectInput overflow fallback.
 * @param {function|null} [onTabsChange=null] - Optional (tabs) => void mirror of unified tabs.
 * @param {boolean} [isNodeLoading=false] - Defer default tab + pane mount until live node is ready.
 */
const PeekPanelTabs = React.memo(({
  node,
  nodeType,
  canEditNode = true,
  isOpen,
  activityPlacement = 'tab',
  activeTabKey: activeTabKeyProp = null,
  setActiveTabKey: setActiveTabKeyProp = null,
  activityRailWidthPx = null,
  onActivityRailWidthChange = null,
  onActivityRailWidthCommit = null,
  pendingAutoTabTypeKey = '',
  setPendingAutoTabTypeKey = null,
  onCoverSaved,
  onLocationSaved,
  onSubtitleSaved,
  onTagsSaved,
  onNodeChange,
  onQuickActionSelect,
  includeFormsTab: includeFormsTabProp = null,
  includeFloorPlanTab = false,
  aboutHostsForms = false,
  typeViewerSurface = 'peek',
  showViewToolbar = true,
  organicViewsEnabled = false,
  onRelatedNodeOpen = null,
  tabsLayout = 'row',
  onTabsChange = null,
  isNodeLoading = false,
}) => {
  // ----- CONTEXT ----- //

  const language = useCurrentLanguage();
  const { theme } = useTheme();
  const { getType, isReady } = useNodeTypes();
  const { user } = useUser();
  const { activeBase } = useBase();

  const clearPendingAutoTabTypeKey = typeof setPendingAutoTabTypeKey === 'function'
    ? setPendingAutoTabTypeKey
    : NOOP_SET_PENDING_AUTO_TAB;

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- HOOKS ----- //

  const {
    peekPanelTabKey,
    setPeekPanelTabKey,
  } = useNodePeek();

  const peekPanelTabKeyResolved = activeTabKeyProp != null
    ? activeTabKeyProp
    : peekPanelTabKey;
  const setPeekPanelTabKeyResolved = setActiveTabKeyProp || setPeekPanelTabKey;

  // ----- STATE ----- //

  const [activeTypeKey, setActiveTypeKey] = useState(null);
  const [isDefaultTabPending, setIsDefaultTabPending] = useState(Boolean(isNodeLoading));
  const [viewOpen, setViewOpen] = useState(false);
  const [editingView, setEditingView] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [relatedSearchQuery, setRelatedSearchQuery] = useState('');
  const [saveOrganicViewOpen, setSaveOrganicViewOpen] = useState(false);
  const [savingOrganicView, setSavingOrganicView] = useState(false);

  // ----- REFS ----- //

  const previousNodeKeyRef = useRef(node?.key || null);
  const stableRelatedGroupsRef = useRef([]);
  // True after an explicit hamburger/tab click this visit — skips About→Highlight redirect.
  const userSelectedPeekTabRef = useRef(false);

  const nodeTypesReady = isReady();

  // ----- MEMOS ----- //

  const showActivityRail = !isMobile && activityPlacement === 'rail';
  const includeActivityTab = activityPlacement === 'none'
    ? false
    : !showActivityRail;
  const forceHighlightTab = shouldShowTypeViewerTab(node?.type, {
    surface: typeViewerSurface,
    nodeType,
  });

  const resolvedTypeKey = useMemo(() => (
    String(nodeType?.key || node?.type || '').trim()
  ), [node?.type, nodeType?.key]);

  // ----- HOOKS ----- //

  const activityTypeLabel = useMemo(() => (
    String(nodeType?.label || '').trim()
  ), [nodeType?.label]);

  const activityCount = useNodeActivityCount(
    includeActivityTab ? node?.key : null,
    { typeLabel: activityTypeLabel },
  );

  const {
    nodeKey,
    childNodes: directChildNodes,
    areChildNodesReady,
  } = useNodeData({
    currentNode: node,
    setCurrentNode: null,
    user,
  });

  const baseKey = node?.baseKey || activeBase?.key || null;

  const grandchildNodes = useRelatedGrandchildNodes({
    hostNode: node,
    childNodes: directChildNodes,
    hostTypeDef: nodeType,
    baseKey,
  });

  const childNodes = useMemo(() => {
    const byKey = new Map();
    [...(Array.isArray(directChildNodes) ? directChildNodes : []),
      ...(Array.isArray(grandchildNodes) ? grandchildNodes : [])].forEach((item) => {
      if (!item?.key || byKey.has(item.key)) return;
      byKey.set(item.key, item);
    });
    return Array.from(byKey.values());
  }, [directChildNodes, grandchildNodes]);

  const { forms: formTemplates, isReady: formsReady } = useNodeForms({
    typeKey: resolvedTypeKey,
    baseKey,
  });

  const hasCustomForms = useMemo(() => (
    formsReady && (Array.isArray(formTemplates) ? formTemplates : []).some((form) => !isDefaultFormTemplate(form))
  ), [formTemplates, formsReady]);

  // Workspace About hosts forms — never show a separate Forms tab there.
  // Peek keeps Forms when custom templates exist (About stays Details-only).
  const includeFormsTab = aboutHostsForms || includeFormsTabProp === false
    ? false
    : (!formsReady || hasCustomForms);

  // ----- MEMOS ----- //

  const visibleChildNodes = useMemo(() => (
    Array.isArray(childNodes) ? childNodes : []
  ), [childNodes]);

  const relatedTypeKeyForToolbar = isRelatedTypeTabKey(peekPanelTabKeyResolved)
    ? peekPanelTabKeyResolved
    : null;

  const lensItems = useMemo(() => {
    const typeKey = String(relatedTypeKeyForToolbar || '').trim();
    if (!typeKey) return [];
    return visibleChildNodes.filter((item) => String(item?.type || '').trim() === typeKey);
  }, [relatedTypeKeyForToolbar, visibleChildNodes]);

  // ----- HOOKS ----- //

  const mobileRelatedGroups = useNodeRelatedGroups({
    childNodes: visibleChildNodes,
    parentTypeDef: nodeType,
    getType,
    language,
  });

  // ----- MEMOS ----- //

  const displayedRelatedGroups = useMemo(() => {
    if (mobileRelatedGroups.length > 0) {
      return mobileRelatedGroups;
    }

    if (
      peekPanelTabKeyResolved !== UNIFIED_TAB_KEYS.ABOUT
      && peekPanelTabKeyResolved !== UNIFIED_TAB_KEYS.ACTIVITY
      && peekPanelTabKeyResolved !== UNIFIED_TAB_KEYS.HIGHLIGHT
      && peekPanelTabKeyResolved !== UNIFIED_TAB_KEYS.FORMS
      && peekPanelTabKeyResolved !== UNIFIED_TAB_KEYS.WORKFLOW
      && stableRelatedGroupsRef.current.length > 0
    ) {
      return stableRelatedGroupsRef.current;
    }

    return mobileRelatedGroups;
  }, [mobileRelatedGroups, peekPanelTabKeyResolved]);

  // ----- HOOKS ----- //

  const {
    activeCatchAllFacets,
    catchAllBoardFields,
    catchAllCalendarFields,
    catchAllFacetFields,
    catchAllFacetSelections,
    catchAllPropertyFields,
    catchAllViewTypes,
    displayedActiveViewIdForBar,
    displayedViewsForBar,
    effectiveActiveView,
    handleCatchAllBoardFieldChange,
    handleCatchAllCalendarFieldChange,
    handleCatchAllFacetSelectionChange,
    handleCatchAllPropertiesChange,
    handleCatchAllViewTypeSelect,
    isCatchAllActive,
    isLoading: isViewsLoading,
    selectedCatchAllBoardField,
    selectedCatchAllCalendarField,
    selectedCatchAllProperties,
    selectedCatchAllViewType,
    setActiveViewId,
    showFieldChooser,
  } = useNodeViewToolbarState({
    node,
    activeTypeKey: relatedTypeKeyForToolbar,
    baseKey,
    organicViewsEnabled,
    lensItems,
  });

  // ----- EFFECTS ----- //

  useEffect(() => {
    setRelatedSearchQuery('');
  }, [node?.key, peekPanelTabKeyResolved, effectiveActiveView?.key]);

  const includeDescendants = Boolean(effectiveActiveView?.includeDescendants);

  // ----- HOOKS ----- //

  const { descendantNodes } = useNodeDescendants({
    nodeKey,
    user,
    parentNode: node,
    enabled: includeDescendants,
  });

  // ----- MEMOS ----- //

  const visibleDescendantNodes = useMemo(() => {
    if (!Array.isArray(descendantNodes) || descendantNodes.length === 0) return [];

    return descendantNodes.filter((item) => {
      if (!nodeTypesReady) return true;
      const typeKey = typeof item?.type === 'string' ? item.type.trim() : '';
      if (!typeKey) return true;
      return Boolean(getType(typeKey));
    });
  }, [descendantNodes, getType, nodeTypesReady]);

  // ----- HANDLERS ----- //

  // Programmatic tab changes must NOT write sessionStorage — that was overwriting
  // the previous node's remembered tab while switching records.
  const applyPeekTabKey = useCallback((nextTabKey) => {
    setPeekPanelTabKeyResolved(nextTabKey);

    if (isRelatedTypeTabKey(nextTabKey)) {
      setActiveTypeKey(nextTabKey);
      return;
    }

    setActiveTypeKey(null);
  }, [setPeekPanelTabKeyResolved]);

  const handlePeekTabUserSelect = useCallback((nextTabKey) => {
    userSelectedPeekTabRef.current = true;
    applyPeekTabKey(nextTabKey);
  }, [applyPeekTabKey]);

  // Withhold panes until default tab is decided (open run? → Workflow : Settings).
  const canShowTabPanes = Boolean(peekPanelTabKeyResolved) && !isDefaultTabPending;
  const isAboutTabActive = canShowTabPanes && peekPanelTabKeyResolved === UNIFIED_TAB_KEYS.ABOUT;
  const isActivityTabActive = canShowTabPanes && peekPanelTabKeyResolved === UNIFIED_TAB_KEYS.ACTIVITY;
  const isHighlightTabActive = canShowTabPanes && peekPanelTabKeyResolved === UNIFIED_TAB_KEYS.HIGHLIGHT;
  const isFormsTabActive = canShowTabPanes && peekPanelTabKeyResolved === UNIFIED_TAB_KEYS.FORMS;
  const isWorkflowTabActive = canShowTabPanes && peekPanelTabKeyResolved === UNIFIED_TAB_KEYS.WORKFLOW;
  const isFloorPlanTabActive = includeFloorPlanTab
    && canShowTabPanes
    && peekPanelTabKeyResolved === UNIFIED_TAB_KEYS.FLOOR_PLAN;
  const isRelatedTabActive = canShowTabPanes && isRelatedTypeTabKey(peekPanelTabKeyResolved);
  // NOTE About: stretch the richtext body into leftover peek height.
  const fillAboutPane = normalizePeekTypeKey(node?.type) === 'NOTE';
  const autoFocusRichText = Boolean(
    fillAboutPane
    && isOpen
    && isAboutTabActive
    && canEditNode !== false
  );
  const richTextAutoFocusKey = String(node?.key || '');

  // ----- MEMOS ----- //

  const activeRelatedGroup = useMemo(() => (
    displayedRelatedGroups.find((group) => group.key === peekPanelTabKeyResolved) || null
  ), [displayedRelatedGroups, peekPanelTabKeyResolved]);

  // ----- HANDLERS ----- //

  /**
   * handleAddView
   *
   * Handles add view.
   */
  const handleAddView = () => {
    setEditingView(null);
    setViewOpen(true);
  };

  /**
   * handleEditView
   *
   * Handles edit view.
   *
   * @param {unknown} view - view.
   */
  const handleEditView = (view) => {
    if (!view) return;
    setEditingView(view);
    setViewOpen(true);
  };

  const organicSaveDefaultTitle = useMemo(() => {
    const boardFieldLabel = catchAllBoardFields.find(
      (field) => field.key === selectedCatchAllBoardField,
    )?.label || '';
    return suggestOrganicViewTitle({
      viewType: selectedCatchAllViewType || effectiveActiveView?.type || 'LIST',
      boardFieldLabel,
      filterCount: Object.keys(catchAllFacetSelections || {}).length,
    });
  }, [
    catchAllBoardFields,
    catchAllFacetSelections,
    effectiveActiveView?.type,
    selectedCatchAllBoardField,
    selectedCatchAllViewType,
  ]);

  /**
   * handleOpenSaveOrganicView
   *
   * Opens the organic Save View dialog.
   */
  const handleOpenSaveOrganicView = useCallback(() => {
    if (!organicViewsEnabled || !isCatchAllActive) return;
    setSaveOrganicViewOpen(true);
  }, [isCatchAllActive, organicViewsEnabled]);

  /**
   * handleSaveOrganicView
   *
   * Persists the current catch-all draft as a Saved View.
   *
   * @param {string} title
   */
  const handleSaveOrganicView = useCallback(async (title) => {
    const userKey = String(user?.key || '').trim();
    const nextTitle = String(title || '').trim();
    const typeKey = String(relatedTypeKeyForToolbar || activeTypeKey || '').trim();
    if (!userKey || !nextTitle || !typeKey) return;

    setSavingOrganicView(true);
    try {
      const viewType = String(
        selectedCatchAllViewType || effectiveActiveView?.type || 'LIST',
      ).trim().toUpperCase();
      const filters = buildFiltersFromLensSelections(
        catchAllFacetSelections,
        catchAllFacetFields,
      );
      const boardField = viewType === 'BOARD'
        ? (selectedCatchAllBoardField || null)
        : null;

      const created = await createView(userKey, viewType, nextTitle, {
        baseKey,
        nodeType: typeKey,
        boardField,
        boardEnumOptions: boardField ? { fieldKey: boardField } : null,
        calendarField: viewType === 'CALENDAR'
          ? (selectedCatchAllCalendarField || null)
          : null,
        filters,
        properties: selectedCatchAllProperties,
        createdFrom: 'NODE',
      });

      if (created?.key) {
        setActiveViewId(created.key);
        toast.success(`Saved as ${nextTitle}`);
        setSaveOrganicViewOpen(false);
      } else {
        toast.error('Could not save view');
      }
    } catch (error) {
      toast.error(`Could not save view: ${error?.message || 'Unknown error'}`);
    } finally {
      setSavingOrganicView(false);
    }
  }, [
    activeTypeKey,
    baseKey,
    catchAllFacetFields,
    catchAllFacetSelections,
    effectiveActiveView?.type,
    relatedTypeKeyForToolbar,
    selectedCatchAllBoardField,
    selectedCatchAllCalendarField,
    selectedCatchAllProperties,
    selectedCatchAllViewType,
    setActiveViewId,
    user?.key,
  ]);

  // ----- EFFECTS ----- //

  // Activity hosted externally (or as rail) — leave a stale Activity tab.
  useEffect(() => {
    if (includeActivityTab) return;
    if (peekPanelTabKeyResolved !== UNIFIED_TAB_KEYS.ACTIVITY) return;
    applyPeekTabKey(
      forceHighlightTab || nodeType?.showHighlight
        ? UNIFIED_TAB_KEYS.HIGHLIGHT
        : UNIFIED_TAB_KEYS.ABOUT
    );
  }, [applyPeekTabKey, forceHighlightTab, includeActivityTab, nodeType?.showHighlight, peekPanelTabKeyResolved]);

  // Forms tab is gated on custom templates.
  // Wait until forms are ready so we don't bounce Forms ↔ About while loading.
  useEffect(() => {
    if (peekPanelTabKeyResolved !== UNIFIED_TAB_KEYS.FORMS) return;
    if (!formsReady || hasCustomForms) return;
    applyPeekTabKey(
      forceHighlightTab || nodeType?.showHighlight
        ? UNIFIED_TAB_KEYS.HIGHLIGHT
        : UNIFIED_TAB_KEYS.ABOUT
    );
  }, [
    applyPeekTabKey,
    forceHighlightTab,
    formsReady,
    hasCustomForms,
    nodeType?.showHighlight,
    peekPanelTabKeyResolved,
  ]);

  // Media / highlight types should land on Highlight, not About — including when
  // type resolves after the first paint (thin seed without type). Do not override
  // an explicit Settings click (Document → Settings was bouncing back here).
  useEffect(() => {
    if (!isOpen || !node?.key) return;
    if (!forceHighlightTab && !nodeType?.showHighlight) return;
    if (peekPanelTabKeyResolved !== UNIFIED_TAB_KEYS.ABOUT) return;
    if (userSelectedPeekTabRef.current) return;

    applyPeekTabKey(UNIFIED_TAB_KEYS.HIGHLIGHT);
  }, [
    applyPeekTabKey,
    forceHighlightTab,
    isOpen,
    node?.key,
    nodeType?.showHighlight,
    peekPanelTabKeyResolved,
  ]);

  // On node change: clear selection only. useUnifiedNodeTabs owns the default tab
  // (open run → Workflow, otherwise Settings) — competing setters caused loops.
  useEffect(() => {
    const nextNodeKey = node?.key || null;

    if (previousNodeKeyRef.current === nextNodeKey) {
      return;
    }

    previousNodeKeyRef.current = nextNodeKey;
    userSelectedPeekTabRef.current = false;
    clearPendingAutoTabTypeKey('');
    setSelectedKeys(new Set());
  }, [node?.key, clearPendingAutoTabTypeKey]);

  useEffect(() => {
    if (isRelatedTypeTabKey(peekPanelTabKeyResolved)) {
      setActiveTypeKey(peekPanelTabKeyResolved);
      return;
    }
    setActiveTypeKey(null);
  }, [peekPanelTabKeyResolved]);

  useEffect(() => {
    stableRelatedGroupsRef.current = [];
  }, [node?.key]);

  useEffect(() => {
    if (mobileRelatedGroups.length > 0) {
      stableRelatedGroupsRef.current = mobileRelatedGroups;
    }
  }, [mobileRelatedGroups]);

  useEffect(() => {
    const pendingTypeKey = normalizePeekTypeKey(pendingAutoTabTypeKey);
    if (!pendingTypeKey) return;

    const matchedGroup = displayedRelatedGroups.find((group) => (
      normalizePeekTypeKey(group?.key) === pendingTypeKey
    ));

    if (!matchedGroup?.key) return;

    applyPeekTabKey(matchedGroup.key);
    clearPendingAutoTabTypeKey('');
  }, [applyPeekTabKey, clearPendingAutoTabTypeKey, displayedRelatedGroups, pendingAutoTabTypeKey]);

  useEffect(() => {
    if (!isRelatedTabActive) return;
    if (activeRelatedGroup?.key) return;

    const matchedGroup = displayedRelatedGroups.find((group) => group.key === peekPanelTabKeyResolved);
    if (matchedGroup?.key) return;

    // Waiting for related groups to load — don't steal the selection.
    if (displayedRelatedGroups.length === 0) return;

    applyPeekTabKey(displayedRelatedGroups[0].key);
  }, [
    activeRelatedGroup?.key,
    applyPeekTabKey,
    displayedRelatedGroups,
    isRelatedTabActive,
    peekPanelTabKeyResolved,
  ]);

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [activeTypeKey, effectiveActiveView?.key, effectiveActiveView?.type, node?.key]);

  if (!node) return null;

  // ----- RENDER ----- //

  return (
    <>
      <div
        className={[
          'peek-panel-workspace',
          showActivityRail ? 'has-activity-rail' : '',
        ].filter(Boolean).join(' ')}
      >
        <div className="peek-panel-workspace-main">
          <div
            className="peek-panel-tabs"
            style={{
              borderTopColor: theme.backgroundMuted,
            }}
          >
            <div className="peek-panel-tabs-row">
              <NodeTabsBar
                key={node?.key || 'peek-tabs'}
                node={node}
                nodeType={nodeType}
                childNodes={visibleChildNodes}
                activeTabKey={peekPanelTabKeyResolved}
                setActiveTabKey={applyPeekTabKey}
                onUserTabSelect={handlePeekTabUserSelect}
                includeAboutTab={true}
                includeActivityTab={includeActivityTab}
                includeFloorPlanTab={includeFloorPlanTab}
                forceHighlightTab={forceHighlightTab}
                // Keep Forms selectable until templates resolve so a remembered
                // Forms session tab isn't invalidated mid-load (About ↔ Forms loop).
                includeFormsTab={includeFormsTab}
                preferAboutTab={true}
                activityCount={activityCount}
                areChildNodesReady={areChildNodesReady}
                isNodeLoading={isNodeLoading}
                persistSessionTab={false}
                showAddRelatedButton={canEditNode !== false}
                tabsLayout={tabsLayout}
                onQuickActionSelect={canEditNode === false ? undefined : onQuickActionSelect}
                onTabsChange={onTabsChange}
                onDefaultTabPendingChange={setIsDefaultTabPending}
              />
            </div>

            {isHighlightTabActive && (
              <div className="peek-panel-tab-pane is-fill">
                {forceHighlightTab ? (
                  <NodeTypeViewer
                    node={node}
                    nodeType={nodeType}
                    readOnly={canEditNode === false}
                    onNodeChange={canEditNode === false ? undefined : onNodeChange}
                    onRelatedNodeOpen={typeof onRelatedNodeOpen === 'function' ? onRelatedNodeOpen : undefined}
                  />
                ) : (
                  <div
                    className="peek-panel-message"
                    style={{ color: theme.foregroundMuted }}
                  >
                    Highlight preview is not available for this type in Peek yet.
                  </div>
                )}
              </div>
            )}

            {isAboutTabActive && (
              <div className={`peek-panel-tab-pane${fillAboutPane ? ' is-fill' : ''}`}>
                {aboutHostsForms ? (
                  <NodeAboutForms
                    node={node}
                    readOnly={canEditNode === false}
                    onCoverSaved={canEditNode === false ? undefined : onCoverSaved}
                    onLocationSaved={canEditNode === false ? undefined : onLocationSaved}
                    onNodeChange={canEditNode === false ? undefined : onNodeChange}
                    onSubtitleSaved={canEditNode === false ? undefined : onSubtitleSaved}
                    onTagsSaved={canEditNode === false ? undefined : onTagsSaved}
                    excludedFieldTypes={PEEK_ABOUT_EXCLUDED_FIELD_TYPES}
                    autoFocusRichText={autoFocusRichText}
                    autoFocusKey={richTextAutoFocusKey}
                  />
                ) : (
                  <NodeDetails
                    node={node}
                    readOnly={canEditNode === false}
                    onCoverSaved={canEditNode === false ? undefined : onCoverSaved}
                    onLocationSaved={canEditNode === false ? undefined : onLocationSaved}
                    onNodeChange={canEditNode === false ? undefined : onNodeChange}
                    onSubtitleSaved={canEditNode === false ? undefined : onSubtitleSaved}
                    onTagsSaved={canEditNode === false ? undefined : onTagsSaved}
                    excludedFieldTypes={PEEK_ABOUT_EXCLUDED_FIELD_TYPES}
                    autoFocusRichText={autoFocusRichText}
                    autoFocusKey={richTextAutoFocusKey}
                  />
                )}
              </div>
            )}

            {includeActivityTab && isActivityTabActive && (
              <div className="peek-panel-tab-pane is-fill">
                <PeekActivitySurface node={node} />
              </div>
            )}

            {isFormsTabActive && (
              <div className="peek-panel-tab-pane">
                <NodeForms
                  node={node}
                  addon="compact"
                  onCoverSaved={onCoverSaved}
                  onLocationSaved={onLocationSaved}
                  onNodeChange={onNodeChange}
                  onSubtitleSaved={onSubtitleSaved}
                  onTagsSaved={onTagsSaved}
                />
              </div>
            )}

            {isWorkflowTabActive && (
              <div className="peek-panel-tab-pane is-fill">
                <NodeMissions
                  node={node}
                  typeDef={nodeType}
                  user={user}
                  addon="compact"
                  onNodeChange={canEditNode === false ? undefined : onNodeChange}
                />
              </div>
            )}

            {isFloorPlanTabActive && (
              <div className="peek-panel-tab-pane is-fill">
                <ProjectFloorPlan
                  node={node}
                  canEdit={canEditNode !== false}
                />
              </div>
            )}

            {isRelatedTabActive && (
              <div
                key={`${node?.key || 'node'}::${peekPanelTabKeyResolved || 'tab'}`}
                className="peek-panel-related-content"
              >
                {!activeRelatedGroup && (
                  <div
                    className="peek-panel-message"
                    style={{
                      color: theme.foregroundMuted,
                    }}
                  >
                    This related section is no longer available.
                  </div>
                )}

                {activeRelatedGroup && (isViewsLoading || !effectiveActiveView) && (
                  <div
                    className="peek-panel-message"
                    style={{
                      color: theme.foregroundMuted,
                    }}
                  >
                    Loading related items...
                  </div>
                )}

                {activeRelatedGroup && !isViewsLoading && effectiveActiveView && (
                  <>
                    {showViewToolbar ? (
                      <div className="node-toolbars node-toolbars-mobile">
                        <div className="node-toolbars-left">
                          <ViewToolbar
                            theme={theme}
                            activeCatchAllViewType={selectedCatchAllViewType}
                            dividerMargin="6px"
                            views={displayedViewsForBar}
                            viewGroups={getType(relatedTypeKeyForToolbar)?.viewGroups || []}
                            activeViewId={displayedActiveViewIdForBar}
                            activeViewType={effectiveActiveView?.type || ''}
                            showFacetControls={isCatchAllActive}
                            facetFields={catchAllFacetFields}
                            facetSelections={catchAllFacetSelections}
                            catchAllViewTypes={catchAllViewTypes}
                            boardFields={catchAllBoardFields}
                            boardFieldKey={selectedCatchAllBoardField}
                            showFieldChooser={showFieldChooser}
                            fieldOptions={catchAllPropertyFields}
                            properties={selectedCatchAllProperties}
                            calendarFields={catchAllCalendarFields}
                            calendarFieldKey={selectedCatchAllCalendarField}
                            showSaveView={organicViewsEnabled && isCatchAllActive}
                            showViewChips={organicViewsEnabled}
                            searchQuery={relatedSearchQuery}
                            onAddView={handleAddView}
                            onBoardFieldChange={handleCatchAllBoardFieldChange}
                            onCalendarFieldChange={handleCatchAllCalendarFieldChange}
                            onChangeView={setActiveViewId}
                            onEditView={handleEditView}
                            onFacetSelectionChange={handleCatchAllFacetSelectionChange}
                            onPropertiesChange={handleCatchAllPropertiesChange}
                            onSaveView={handleOpenSaveOrganicView}
                            onSearchQueryChange={setRelatedSearchQuery}
                            onSelectCatchAllViewType={handleCatchAllViewTypeSelect}
                          />
                        </div>
                      </div>
                    ) : null}

                    <div className="peek-panel-related-scroll">
                      <NodeViewContent
                        node={node}
                        childNodes={visibleChildNodes}
                        descendantNodes={visibleDescendantNodes}
                        activeTypeKey={activeRelatedGroup.key}
                        view={effectiveActiveView}
                        searchQuery={relatedSearchQuery}
                        activeCatchAllFacets={isCatchAllActive ? activeCatchAllFacets : []}
                        selectedKeys={selectedKeys}
                        setSelectedKeys={setSelectedKeys}
                        tableShowCheckboxes={false}
                        onActiveTypeKeyChange={setActiveTypeKey}
                        onItemClick={typeof onRelatedNodeOpen === 'function' ? onRelatedNodeOpen : null}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {showActivityRail ? (
          <>
            <ActivityRailResizeHandle
              widthPx={activityRailWidthPx || 340}
              onWidthChange={onActivityRailWidthChange}
              onWidthCommit={onActivityRailWidthCommit}
            />
            <aside
              className="peek-panel-activity-rail"
              style={{
                borderLeftColor: theme.backgroundMuted,
                flex: activityRailWidthPx
                  ? `0 0 ${activityRailWidthPx}px`
                  : undefined,
                width: activityRailWidthPx ? activityRailWidthPx : undefined,
                maxWidth: activityRailWidthPx ? activityRailWidthPx : undefined,
              }}
            >
              <PeekActivitySurface
                node={node}
                showHeader
              />
            </aside>
          </>
        ) : null}
      </div>

      <ViewModal
        isOpen={viewOpen}
        setOpen={setViewOpen}
        typeKey={activeTypeKey || node?.type || ''}
        parentTypeKey={node?.type || ''}
        baseKey={baseKey}
        createdFrom="NODE"
        mode={editingView ? 'edit' : 'create'}
        view={editingView}
        onSave={(savedView) => {
          const savedViewId = savedView?.key || null;
          if (savedViewId) setActiveViewId(savedViewId);
          setViewOpen(false);
        }}
      />

      <SaveOrganicViewModal
        isOpen={saveOrganicViewOpen}
        setOpen={setSaveOrganicViewOpen}
        defaultTitle={organicSaveDefaultTitle}
        saving={savingOrganicView}
        onSave={handleSaveOrganicView}
      />
    </>
  );
});

// Dev Tools
PeekPanelTabs.displayName = 'PeekPanelTabs';

export default PeekPanelTabs;
